import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useRef, useState } from 'react';

import { poolAutomationClient } from './api';

import { apiErrorMessage } from '@/routes/Agency/DashboardV2/util/apiError';

// One root for every pool automation read, so any write refreshes all of them:
// turning a client's auto-invite off changes what each of its jobs' dispatch
// reads, and a pause changes what the eligibility list means.
const ROOT = 'pool_automation' as const;

export const poolAutomationKeys = {
  root: [ROOT] as const,
  poolAutoInvite: (customerId: number | string) =>
    [ROOT, 'customer', String(customerId), 'pool_auto_invite'] as const,
  unfilledNotice: (customerId: number | string) =>
    [ROOT, 'customer', String(customerId), 'unfilled_notice'] as const,
  dispatch: (jobId: number | string) =>
    [ROOT, 'job', String(jobId), 'dispatch'] as const,
  eligibility: (jobId: number | string) =>
    [ROOT, 'job', String(jobId), 'eligibility'] as const,
};

export const usePoolAutoInvite = (
  customerId: number | string,
  enabled = true,
) =>
  useQuery({
    queryKey: poolAutomationKeys.poolAutoInvite(customerId),
    queryFn: () => poolAutomationClient.getPoolAutoInvite(customerId),
    enabled,
    retry: false,
  });

export const useUnfilledNotice = (
  customerId: number | string,
  enabled = true,
) =>
  useQuery({
    queryKey: poolAutomationKeys.unfilledNotice(customerId),
    queryFn: () => poolAutomationClient.getUnfilledNotice(customerId),
    enabled,
    retry: false,
  });

export const usePoolDispatch = (
  jobId: number | string | undefined,
  enabled = true,
) =>
  useQuery({
    queryKey: poolAutomationKeys.dispatch(jobId ?? ''),
    queryFn: () => poolAutomationClient.getDispatch(jobId as string),
    enabled: enabled && jobId != null,
    retry: false,
  });

export const usePoolEligibility = (
  jobId: number | string | undefined,
  enabled = true,
) =>
  useQuery({
    queryKey: poolAutomationKeys.eligibility(jobId ?? ''),
    queryFn: () => poolAutomationClient.getEligibility(jobId as string),
    enabled: enabled && jobId != null,
    retry: false,
  });

/**
 * One write. Returns false on failure and leaves the API's own message
 * (`errors[0]`, e.g. the pause refusals) in `error` for the control to show
 * inline.
 *
 * Every outcome refreshes all pool automation reads AND Dashboard V2: a 422
 * usually means the state moved under the screen (the client turned
 * auto-invite off, the job started), so the screen must re-read either way.
 */
export const usePoolSave = <A, T>(
  write: (arg: A) => Promise<T>,
  fallback: string,
  onSaved?: (result: T, arg: A) => void,
) => {
  const queryClient = useQueryClient();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const latest = useRef({ write, onSaved });
  latest.current = { write, onSaved };

  const save = useCallback(
    async (arg: A): Promise<boolean> => {
      setSaving(true);
      setError(null);
      try {
        const result = await latest.current.write(arg);
        latest.current.onSaved?.(result, arg);
        return true;
      } catch (e) {
        setError(apiErrorMessage(e) ?? fallback);
        return false;
      } finally {
        setSaving(false);
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: poolAutomationKeys.root }),
          queryClient.invalidateQueries({ queryKey: ['dashboard_v2'] }),
        ]);
      }
    },
    [fallback, queryClient],
  );

  return { save, saving, error };
};

/**
 * Holds an on/off change until its warning is confirmed. Both directions ask
 * first: turning either setting on or off changes what GravyWork sends to
 * workers or to the client from then on.
 */
export const useSwitchChoice = (save: (next: boolean) => Promise<boolean>) => {
  const [choice, setChoice] = useState<boolean | null>(null);

  const request = useCallback((next: boolean) => setChoice(next), []);
  const cancel = useCallback(() => setChoice(null), []);
  const confirm = useCallback(async () => {
    if (choice == null) return;
    const next = choice;
    setChoice(null);
    await save(next);
  }, [choice, save]);

  return { choice, request, cancel, confirm };
};

export type SwitchChooser = ReturnType<typeof useSwitchChoice>;
